import React, { useEffect } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';

import { userSelector } from '../../reducers/current-user-selectors';
import { accountSelector } from '../account-selectors';
import { loadAccount } from '../actions/load-account';
import { updateAccount } from '../actions/update-account';

import { validatePhone } from '../../businesslogic/form-validators';
import { UiButton } from '../../ui/ui-button';

import {
  ACCOUNT_BUSINESS_INDUSTRIES,
  TEAM_ENGINE_INDUSTRY_TO_TWILIO_INDUSTRY,
} from '../../admin/util/account-business-industries';
import { ACCOUNT_BUSINESS_REGION_OF_OPERATIONS } from '../../admin/util/account-business-region-of-operations';
import { ACCOUNT_BUSINESS_REGISTRATION_NUMBER_TYPE } from '../../admin/util/account-business-registration-number-type';
import { ACCOUNT_BUSINESS_STATUS } from '../../admin/util/account-business-status';
import { ACCOUNT_STOCK_EXCHANGE } from '../../admin/util/account-business-stock-exchange';
import { ACCOUNT_BUSINESS_TYPE } from '../../admin/util/account-business-type';
import { convertPhoneNumberToE164 } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { isValidHttpUrl, EIN_REGEX } from '../../util/string-validators';
import { FormSelect } from '../../widgets/react-hook-form-components/form-select';
import { FormStateAutocomplete } from '../../widgets/react-hook-form-components/form-state-autocomplete';
import { FormTextField } from '../../widgets/react-hook-form-components/form-text-field';

interface InputProps {
  accountId: number;
  org?: Org;
  onSave?: () => void;
}

const mapStateToProps = (state: any, { accountId }: InputProps) => ({
  account: accountSelector(state, accountId),
  currentUser: userSelector(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { accountId }: InputProps,
) => ({
  actions: {
    loadAccount: () => dispatch(loadAccount(accountId)),
    updateAccount: (account: TeamEngineAccountUpdateRequest) =>
      dispatch(updateAccount(accountId, account)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const TwilioBusinessInformationForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ account, org, currentUser, onSave, actions }: Props) => {
  const [isTriggered, setIsTriggered] = React.useState(false);

  useEffect(() => {
    actions.loadAccount();
  });

  if (!account) {
    return null;
  }

  const defaultValues = {
    businessName: account.businessName || org?.name || '',
    businessWebsite: account.website || org?.website || '',
    businessStreet: account.businessStreet || '',
    businessUnit: account.businessUnit || '',
    businessCity: account.businessCity || '',
    businessState: account.businessState || '',
    businessZipCode: account.businessZipCode || '',
    businessType: account.businessType ?? '',
    businessStatus: account.businessStatus ?? 'PRIVATE',
    businessStockTicker: account.businessStockTicker ?? '',
    businessRegistrationNumber: account.businessRegistrationNumber ?? '',
    businessRegistrationNumberType: account.businessRegistrationNumberType ?? 'USA_EIN',
    businessIndustry: account.businessIndustry ?? '',
    businessRegionOfOperations: account.businessRegionOfOperations?.split(',') ?? [
      'USA_AND_CANADA',
    ],
    businessStockExchange: account.businessStockExchange ?? '',
    businessPhoneNumber: account.businessPhoneNumber ?? '',
  };

  const {
    control,
    setValue,
    errors,
    formState: { isDirty },
    handleSubmit,
    watch,
    reset,
    trigger,
  } = useForm({ mode: 'all', defaultValues });

  const hasErrors = Object.keys(errors).length > 0;

  const businessStatusWatch = watch('businessStatus');
  const businessIndustryWatch = watch('businessIndustry');
  const businessWebsiteWatch = watch('businessWebsite');
  const businessRegistrationNumberTypeWatch = watch('businessRegistrationNumberType');

  const isPublicBusiness = Boolean(
    businessStatusWatch &&
      ACCOUNT_BUSINESS_STATUS[businessStatusWatch as keyof typeof ACCOUNT_BUSINESS_STATUS] ===
        ACCOUNT_BUSINESS_STATUS.PUBLIC,
  );

  // Set the default industry based on the org's information. Do it in a use effect because we have to do it one time after the orgs are loaded, but
  // don't want to cause an infinite loop
  useEffect(() => {
    if (account && org && businessIndustryWatch === '') {
      const defaultIndustry =
        TEAM_ENGINE_INDUSTRY_TO_TWILIO_INDUSTRY[
          org.industryId as keyof typeof TEAM_ENGINE_INDUSTRY_TO_TWILIO_INDUSTRY
        ];
      if (org.industryId && defaultIndustry) {
        setValue('businessIndustry', defaultIndustry);
      }
    }
  }, [account, actions, businessIndustryWatch, org, org?.industryId, setValue]);

  // Trigger the form to show validation when the form is loaded. We want it to be obvious what needs to be filled out, as it will be partially filled in
  // by the AEs
  useEffect(() => {
    if (!isTriggered) {
      trigger();
      setIsTriggered(true);
    }
  }, [isTriggered, trigger]);

  const onSubmit: SubmitHandler<typeof defaultValues> = async ({
    businessPhoneNumber,
    businessRegionOfOperations,
    businessWebsite,
    ...rest
  }) => {
    const update = {
      ...rest,
      website: businessWebsite,
      businessPhoneNumber:
        (businessPhoneNumber && !validatePhone(businessPhoneNumber)
          ? convertPhoneNumberToE164(businessPhoneNumber)
          : null) || '',
      businessRegionOfOperations: businessRegionOfOperations?.join(','),
    };
    actions.updateAccount(update);
    reset({
      ...defaultValues,
      ...{
        ...update,
        businessRegionOfOperations,
      },
    });
    if (onSave) {
      onSave();
    }
  };

  let businessRegistrationNumberFormat = '';
  if (businessRegistrationNumberTypeWatch === 'USA_EIN') {
    businessRegistrationNumberFormat = '(XX-XXXXXXX)';
  }

  return (
    <div className="u-maxWidth-1000px u-margin-0Auto">
      <Paper className="u-padding" variant="outlined">
        <div className="typo-label">Basic Information</div>
        <div className="u-width-100">
          <FormTextField
            control={control}
            className="u-width-100"
            label="Legal Business Name (as it appears on business registration or tax forms)"
            name="businessName"
            error={errors.businessName}
            required
          />
        </div>
        <div className="u-width-100 u-flex-center u-justifyContent-center">
          <FormTextField
            control={control}
            className="u-width-100"
            label="Website"
            name="businessWebsite"
            error={errors.businessWebsite}
            required
          />
          {businessWebsiteWatch && isValidHttpUrl(businessWebsiteWatch) && currentUser.isAdmin ? (
            <a
              href={businessWebsiteWatch}
              className="typo-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open"
            >
              <MaterialDesignIcon iconName={ICON_NAMES.OPEN_IN_NEW} />
            </a>
          ) : null}
        </div>
        <div className="u-width-100">
          <FormTextField
            control={control}
            className="u-width-100"
            label="Phone Number"
            name="businessPhoneNumber"
            textFieldVariant="phone"
            error={errors.businessPhoneNumber}
            required
          />
        </div>
        <div className="typo-label u-margin-y">Business Address</div>
        <div className="typo-subtitle u-margin-y">
          This is the primary location you do business. Use the location of your headquarters, or
          the primary place that customers would go to interact with your business.
        </div>
        <div className="u-display-flex">
          <FormTextField
            control={control}
            className="u-width-100 u-margin-right"
            label="Street"
            name="businessStreet"
            error={errors.businessStreet}
            required
          />
          <FormTextField
            control={control}
            className="u-width-100 u-margin-left"
            label="Unit"
            name="businessUnit"
            error={errors.businessUnit}
          />
        </div>
        <div className="u-flex-center">
          <FormTextField
            control={control}
            className="u-width-100 u-margin-right"
            label="City"
            name="businessCity"
            error={errors.businessCity}
            required
          />
          <div className="u-width-100 u-margin-x">
            <FormStateAutocomplete
              control={control}
              name="businessState"
              label="State"
              error={errors.businessState}
              required
            />
          </div>
          <FormTextField
            control={control}
            className="u-width-100 u-margin-left"
            label="Zip Code"
            name="businessZipCode"
            error={errors.businessZipCode}
            required
            rules={{
              // I tried to reuse the ZIP_CODE_REGEX from the string-validators, but
              // it broke editing for a reason I didn't bother to track down, because
              // this works
              pattern: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
            }}
          />
        </div>
        <div className="typo-label u-margin-y">Business Categorization</div>
        <div className="typo-subtitle u-margin-y">
          This data lets the carriers know how to categorize your business. This helps them
          determine your messaging throughput and lets them track how much traffic they can
          attribute to various industries.
        </div>
        <div className="u-flex-center">
          <FormSelect
            control={control}
            className="u-width-100 u-margin-right"
            label="Business Type"
            name="businessType"
            error={errors.businessType}
            required
          >
            <MenuItem value="">Select a business type...</MenuItem>
            {Object.keys(ACCOUNT_BUSINESS_TYPE).map((businessTypeOption) => (
              <MenuItem key={businessTypeOption} value={businessTypeOption}>
                {ACCOUNT_BUSINESS_TYPE[businessTypeOption as keyof typeof ACCOUNT_BUSINESS_TYPE]}
              </MenuItem>
            ))}
          </FormSelect>
          <FormSelect
            control={control}
            className={`u-width-100 u-margin-${
              businessStatusWatch === ACCOUNT_BUSINESS_STATUS.PUBLIC ? 'x' : 'left'
            }`}
            label="Business Status"
            name="businessStatus"
            error={errors.businessStatus}
          >
            <MenuItem value="">Select a business status...</MenuItem>
            {Object.keys(ACCOUNT_BUSINESS_STATUS).map((businessStatusOption) => (
              <MenuItem key={businessStatusOption} value={businessStatusOption}>
                {
                  ACCOUNT_BUSINESS_STATUS[
                    businessStatusOption as keyof typeof ACCOUNT_BUSINESS_STATUS
                  ]
                }
              </MenuItem>
            ))}
          </FormSelect>
          <FormTextField
            control={control}
            className={isPublicBusiness ? 'u-width-100 u-margin-left' : 'u-display-none'}
            label="Stock Ticker"
            name="businessStockTicker"
            error={errors.businessStockTicker}
            required={isPublicBusiness}
          />
          <FormSelect
            control={control}
            className={isPublicBusiness ? 'u-width-100 u-margin-x' : 'u-display-none'}
            label="Stock Exchange"
            name="businessStockExchange"
            error={errors.businessStockExchange}
            required={isPublicBusiness}
          >
            <MenuItem value="">Select a business status...</MenuItem>
            {Object.keys(ACCOUNT_STOCK_EXCHANGE).map((businessStockExchangeOption) => (
              <MenuItem key={businessStockExchangeOption} value={businessStockExchangeOption}>
                {
                  ACCOUNT_STOCK_EXCHANGE[
                    businessStockExchangeOption as keyof typeof ACCOUNT_STOCK_EXCHANGE
                  ]
                }
              </MenuItem>
            ))}
          </FormSelect>
        </div>
        <div className="u-flex-center">
          <FormSelect
            control={control}
            className="u-width-100 u-margin-right"
            label="Business Industry"
            name="businessIndustry"
            error={errors.businessIndustry}
            required
          >
            <MenuItem value="">Select an industry...</MenuItem>
            {Object.keys(ACCOUNT_BUSINESS_INDUSTRIES).map((businessIndustryOption) => (
              <MenuItem key={businessIndustryOption} value={businessIndustryOption}>
                {
                  ACCOUNT_BUSINESS_INDUSTRIES[
                    businessIndustryOption as keyof typeof ACCOUNT_BUSINESS_INDUSTRIES
                  ]
                }
              </MenuItem>
            ))}
          </FormSelect>
          <FormSelect
            control={control}
            className="u-width-100 u-margin-left"
            label="Business Region Of Operations"
            name="businessRegionOfOperations"
            multiple
            required
          >
            <MenuItem value="">Select a region of operation...</MenuItem>
            {Object.keys(ACCOUNT_BUSINESS_REGION_OF_OPERATIONS).map(
              (businessRegionOfOperationOption) => (
                <MenuItem
                  key={businessRegionOfOperationOption}
                  value={businessRegionOfOperationOption}
                >
                  {
                    ACCOUNT_BUSINESS_REGION_OF_OPERATIONS[
                      businessRegionOfOperationOption as keyof typeof ACCOUNT_BUSINESS_REGION_OF_OPERATIONS
                    ]
                  }
                </MenuItem>
              ),
            )}
          </FormSelect>
        </div>
        <div className="typo-label u-margin-y">Registration Number</div>
        <div className="typo-subtitle u-margin-y">
          Most customers will provide their EIN below. This information is used to verify that you
          are a business operating in the US, and not someone looking to send spam or phishing
          messages.
        </div>
        <div className="u-flex-center">
          <FormSelect
            control={control}
            className="u-width-100 u-margin-right"
            label="Business Registration Number Type"
            name="businessRegistrationNumberType"
            error={errors.businessRegistrationNumberType}
            required
          >
            <MenuItem value="">Select a registration number type...</MenuItem>
            {Object.keys(ACCOUNT_BUSINESS_REGISTRATION_NUMBER_TYPE).map(
              (businessRegistrationNumberTypeOption) => (
                <MenuItem
                  key={businessRegistrationNumberTypeOption}
                  value={businessRegistrationNumberTypeOption}
                >
                  {
                    ACCOUNT_BUSINESS_REGISTRATION_NUMBER_TYPE[
                      businessRegistrationNumberTypeOption as keyof typeof ACCOUNT_BUSINESS_REGISTRATION_NUMBER_TYPE
                    ]
                  }
                </MenuItem>
              ),
            )}
          </FormSelect>
          <FormTextField
            control={control}
            className="u-width-100 u-margin-left"
            label={`Business Registration Number ${businessRegistrationNumberFormat}`}
            name="businessRegistrationNumber"
            error={errors.businessRegistrationNumber}
            required
            rules={{
              validate: {
                isEinIfTypeIsRight: (value) => {
                  if (businessRegistrationNumberTypeWatch === 'USA_EIN') {
                    const regex = EIN_REGEX();
                    if (!regex.test(value)) {
                      return 'Invalid EIN';
                    }
                  }
                  return true;
                },
              },
            }}
          />
        </div>
        <div className="u-padding-top u-display-flex u-justifyContent-flexEnd">
          <UiButton onClick={handleSubmit(onSubmit)} disabled={hasErrors || !isDirty}>
            Save
          </UiButton>
        </div>
      </Paper>
    </div>
  );
});
